import { Language } from "./types";

export type TranslationKey =
  // Navigation
  | "nav.home"
  | "nav.portfolio"
  | "nav.about"
  | "nav.services"
  | "nav.getInTouch"
  | "nav.bookNow"
  | "nav.allWork"

  // Home page
  | "home.recentWork"
  | "home.viewFullPortfolio"
  | "home.readyToCapture"
  | "home.captureYourStory"
  | "home.getInTouch"
  | "home.loading"
  | "home.noRecentWork"

  // Portfolio page
  | "portfolio.title"
  | "portfolio.subtitle"
  | "portfolio.loading"
  | "portfolio.emptyState"
  | "portfolio.allWork"

  // Services page
  | "services.title"
  | "services.subtitle"
  | "services.readyToBook"
  | "services.contactForQuote"
  | "services.inquireNow"
  | "services.getInTouch"
  | "services.startingAt"

  // Service names
  | "services.wedding.title"
  | "services.wedding.description"
  | "services.portrait.title"
  | "services.portrait.description"
  | "services.commercial.title"
  | "services.commercial.description"
  | "services.event.title"
  | "services.event.description"
  | "services.car.title"
  | "services.car.description"
  | "services.family.title"
  | "services.family.description"

  // Service features
  | "services.wedding.feature.1"
  | "services.wedding.feature.2"
  | "services.wedding.feature.3"
  | "services.wedding.feature.4"
  | "services.wedding.feature.5"
  | "services.wedding.feature.6"
  | "services.portrait.feature.1"
  | "services.portrait.feature.2"
  | "services.portrait.feature.3"
  | "services.portrait.feature.4"
  | "services.portrait.feature.5"
  | "services.portrait.feature.6"
  | "services.commercial.feature.1"
  | "services.commercial.feature.2"
  | "services.commercial.feature.3"
  | "services.commercial.feature.4"
  | "services.commercial.feature.5"
  | "services.commercial.feature.6"
  | "services.event.feature.1"
  | "services.event.feature.2"
  | "services.event.feature.3"
  | "services.event.feature.4"
  | "services.event.feature.5"
  | "services.event.feature.6"
  | "services.car.feature.1"
  | "services.car.feature.2"
  | "services.car.feature.3"
  | "services.car.feature.4"
  | "services.car.feature.5"
  | "services.car.feature.6"
  | "services.family.feature.1"
  | "services.family.feature.2"
  | "services.family.feature.3"
  | "services.family.feature.4"
  | "services.family.feature.5"
  | "services.family.feature.6"

  // About page
  | "about.title"
  | "about.subtitle"
  | "about.story.title"
  | "about.story.text1"
  | "about.story.text2"
  | "about.passion.title"
  | "about.passion.text1"
  | "about.passion.text2"
  | "about.passion.text3"
  | "about.mission.title"
  | "about.mission.text"
  | "about.approach.title"
  | "about.approach.text1"
  | "about.approach.text2"

  // Contact/Email modal
  | "contact.email.title"
  | "contact.email.subtitle"
  | "contact.email.copy"
  | "contact.email.copied"
  | "contact.email.close"

  // Common
  | "common.loading"
  | "common.error"
  | "common.close"
  | "common.next"
  | "common.previous"
  | "common.view"
  | "common.images"
  | "common.verify"

  // Shared Project page
  | "sharedProject.back"
  | "sharedProject.projectNotFound"
  | "sharedProject.projectNotFoundMessage";

export interface Translations {
  [key: string]: string;
}

// Cache for loaded translations
let translationCache: Record<Language, Translations> = {
  en: {},
  de: {}
};

// Track which languages have been loaded
const loadedLanguages: Set<Language> = new Set();

/**
 * Dynamically load translations for a specific language
 */
export async function loadTranslations(language: Language): Promise<Translations> {
  // If already loaded, return cached version
  if (loadedLanguages.has(language)) {
    return translationCache[language];
  }

  try {
    // Dynamically import the language file
    const translationsModule = await import(`./locales/${language}.ts`);
    const translations = translationsModule.default || translationsModule;

    // Cache the translations
    translationCache[language] = translations;
    loadedLanguages.add(language);

    return translations;
  } catch (error) {
    console.error(`Failed to load translations for ${language}:`, error);
    // Return empty object as fallback
    return {};
  }
}

/**
 * Get translations for a specific language (synchronous, assumes already loaded)
 */
export function getTranslations(language: Language): Translations {
  return translationCache[language] || {};
}

/**
 * Check if a language's translations are loaded
 */
export function isLanguageLoaded(language: Language): boolean {
  return loadedLanguages.has(language);
}

/**
 * Preload translations for multiple languages
 */
export async function preloadTranslations(languages: Language[]): Promise<void> {
  await Promise.all(languages.map(lang => loadTranslations(lang)));
}

// Export the TranslationKey type for use in components
export type { Language };
